import { useCallback, useEffect, useState } from 'react';
import { cccdAdmin, type CatalogItemCategory, type CccdCatalogItem } from '@/lib/api/cccdAdminClient';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { Toggle } from '@/components/admin/FormComponents';
import { TrashBinIcon, CheckLineIcon } from '@/icons';
import { toastError, toastSuccess } from '@/lib/toast';
import { showUndoToast } from '@/lib/undoToast';
import { LoadState } from "@/components/ui/LoadState"

const cellInputCls =
  'h-[38px] w-full rounded-lg border border-gray-300 px-3 text-[13px] text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';

const selectCls = `${cellInputCls} appearance-none`;

const PAGE_SIZE = 10;

const CATEGORY_LABELS: Record<CatalogItemCategory, string> = {
  structure: 'Estructuras',
  electronics: 'Electrónica',
  launch_equipment: 'Equip. de lanzamiento',
  merchandising: 'Merchandising',
};
const CATEGORIES = Object.keys(CATEGORY_LABELS) as CatalogItemCategory[];

type EditableField = 'name' | 'description' | 'imageUrl' | 'priceCop' | 'category';
type ItemEdits = Partial<Record<EditableField, string | number>>;

function usageLabel(count: number): string {
  if (count === 0) return 'Sin usar';
  if (count === 1) return '1 evento';
  return `${count} eventos`;
}

// Catálogo global de construcción/decoración CCCD (cccd-team-supply-catalog):
// entidad separada del catálogo de complementos de inscripción
// (AddonsCatalogPage) — nunca se cobra, solo alimenta CccdSupplyRequest.
// Mismo diseño inline-editable fila-por-fila (cccd-admin-design-system).
export default function CatalogItemsPage() {
  const [items, setItems] = useState<CccdCatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [edits, setEdits] = useState<Record<string, ItemEdits>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newPriceCop, setNewPriceCop] = useState('');
  const [newCategory, setNewCategory] = useState<CatalogItemCategory>('structure');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await cccdAdmin.listCatalogItems());
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al cargar el catálogo de construcción.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function displayValue(item: CccdCatalogItem, field: EditableField): string | number {
    return edits[item.id]?.[field] ?? (item[field] ?? '');
  }

  function stageEdit(item: CccdCatalogItem, field: EditableField, raw: string) {
    const value: string | number = field === 'priceCop' ? (Number(raw) || 0) : raw;
    setEdits((prev) => {
      const current = { ...(prev[item.id] ?? {}) };
      if (value === (item[field] ?? '')) {
        delete current[field];
      } else {
        current[field] = value;
      }
      const next = { ...prev };
      if (Object.keys(current).length === 0) delete next[item.id];
      else next[item.id] = current;
      return next;
    });
  }

  async function saveRow(item: CccdCatalogItem) {
    const patch = edits[item.id];
    if (!patch) return;
    setSavingId(item.id);
    try {
      await cccdAdmin.updateCatalogItem(item.id, patch as never);
      setEdits((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      toastSuccess(`"${item.name}" actualizado.`);
      await load();
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? `No se pudo guardar "${item.name}".`);
    } finally {
      setSavingId(null);
    }
  }

  async function onCreate() {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await cccdAdmin.createCatalogItem({
        name,
        description: newDescription.trim(),
        imageUrl: newImageUrl.trim() || undefined,
        priceCop: Number(newPriceCop) || 0,
        category: newCategory,
        sortOrder: items.length,
      });
      setNewName('');
      setNewDescription('');
      setNewImageUrl('');
      setNewPriceCop('');
      await load();
      setPage(Math.ceil((items.length + 1) / PAGE_SIZE));
      toastSuccess(`"${name}" agregado al catálogo.`);
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al crear el artículo.');
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(item: CccdCatalogItem) {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, isActive: !i.isActive } : i)));
    try {
      await cccdAdmin.toggleCatalogItem(item.id, !item.isActive);
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al actualizar el artículo.');
      await load();
    }
  }

  // Igual que AddonsCatalogPage: elimina de inmediato en la UI y difiere el
  // DELETE (soft delete vía isActive: false) hasta que expire "Deshacer".
  function handleDelete(item: CccdCatalogItem) {
    const snapshot = items;
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    showUndoToast({
      message: `Artículo "${item.name}" eliminado`,
      onUndo: () => setItems(snapshot),
      onCommit: async () => {
        try {
          await cccdAdmin.deleteCatalogItem(item.id);
        } catch (err: any) {
          setItems(snapshot);
          toastError(err?.response?.data?.error ?? 'No se pudo eliminar el artículo.');
        }
      },
    });
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Catálogo de construcción']}
      title="Catálogo de construcción"
      subtitle="Artículos de ALAS Corp para construir o decorar el cohete (estructuras, electrónica, equipamiento de lanzamiento, merchandising) — no se cobran aquí, solo generan una solicitud del equipo. Habilítalos por evento desde la ficha de cada evento."
      tabs={[]}
      metrics={[]}
    >
      <div className="mb-5 rounded-2xl border border-dashed border-gray-300 bg-gray-25 p-5 dark:border-gray-700 dark:bg-white/[0.02]">
        <p className="mb-3 text-sm font-semibold text-gray-600 dark:text-gray-400">Agregar artículo nuevo</p>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Nombre</label>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ej. Fuselaje BT-55" className={cellInputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Categoría</label>
            <select value={newCategory} onChange={(e) => setNewCategory(e.target.value as CatalogItemCategory)} className={selectCls}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Precio COP</label>
            <input type="number" min={0} value={newPriceCop} onChange={(e) => setNewPriceCop(e.target.value)} placeholder="0" className={cellInputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Imagen (URL)</label>
            <input value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)} placeholder="https://…" className={cellInputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Descripción</label>
            <input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Opcional" className={cellInputCls} />
          </div>
        </div>
        <button
          type="button"
          onClick={onCreate}
          disabled={!newName.trim() || creating}
          className="mt-3 h-10 whitespace-nowrap rounded-lg bg-brand-500 px-[18px] text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-60"
        >
          {creating ? 'Agregando…' : '+ Agregar'}
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="grid min-w-[920px] grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_auto_auto] gap-3 border-b border-gray-100 bg-gray-25 px-5 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
          {['Artículo', 'Categoría', 'Precio COP', 'Imagen (URL)', 'Descripción', 'Usado en', 'Activo', ''].map((h, i) => (
            <span key={i} className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{h}</span>
          ))}
        </div>

        {loading ? (
          <LoadState />
        ) : pageItems.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Sin artículos todavía.</p>
        ) : (
          pageItems.map((item) => {
            const isDirty = Boolean(edits[item.id]);
            const isSaving = savingId === item.id;
            return (
              <div key={item.id} className="grid min-w-[920px] grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_auto_auto] items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-b-0 dark:border-gray-800">
                <input type="text" value={displayValue(item, 'name')} onChange={(e) => stageEdit(item, 'name', e.target.value)} className={cellInputCls} />
                <select value={displayValue(item, 'category')} onChange={(e) => stageEdit(item, 'category', e.target.value)} className={selectCls}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                </select>
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">$</span>
                  <input type="number" min={0} value={displayValue(item, 'priceCop')} onChange={(e) => stageEdit(item, 'priceCop', e.target.value)} className={`${cellInputCls} pl-5`} />
                </div>
                <input type="text" value={displayValue(item, 'imageUrl')} onChange={(e) => stageEdit(item, 'imageUrl', e.target.value)} className={cellInputCls} />
                <input type="text" value={displayValue(item, 'description')} onChange={(e) => stageEdit(item, 'description', e.target.value)} className={cellInputCls} />
                <span className="w-fit rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-500">
                  {usageLabel(item._count.eventLinks)}
                </span>
                <Toggle checked={item.isActive} onChange={() => toggleActive(item)} label="" />
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => saveRow(item)}
                    disabled={!isDirty || isSaving}
                    title={isDirty ? 'Guardar cambios' : 'Sin cambios'}
                    className={`flex size-7 items-center justify-center rounded-lg border transition-colors ${
                      isDirty
                        ? 'border-brand-100 bg-brand-50 text-brand-500 hover:bg-brand-100 dark:border-brand-500/20 dark:bg-brand-500/15 dark:text-brand-400'
                        : 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-600'
                    }`}
                  >
                    <CheckLineIcon className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    title="Eliminar"
                    className="flex size-7 items-center justify-center rounded-lg border border-gray-100 text-gray-400 hover:bg-error-50 hover:text-error-600 dark:border-gray-800 dark:hover:bg-error-500/10"
                  >
                    <TrashBinIcon className="size-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>Página {currentPage} de {totalPages} · {items.length} artículos</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </ModuleLayout>
  );
}
